import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { rmApi as R, CATEGORY_LABEL, inr } from "@/lib/rawMaterials";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Loader2, AlertTriangle } from "lucide-react";

const TYPE_STYLE = {
  grn: "bg-emerald-50 text-emerald-700",
  issue: "bg-indigo-50 text-indigo-700",
  adjustment: "bg-amber-50 text-amber-700",
};

export default function MaterialDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  useEffect(() => { R.getMaterial(id).then(setM).catch(() => { toast.error("Not found"); navigate("/raw-materials"); }); }, [id, navigate]);
  if (!m) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  return (
    <div className="space-y-6" data-testid="material-detail-page">
      <button onClick={() => navigate("/raw-materials")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" />Back to raw materials</button>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{m.name}</h2>
        <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">{m.code}</Badge>
        <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">{CATEGORY_LABEL[m.category]}</Badge>
        {m.low && <Badge className="bg-rose-50 text-rose-700 hover:bg-rose-50"><AlertTriangle className="mr-1 h-3 w-3" />Low stock</Badge>}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="p-4" data-testid="md-onhand"><p className="text-xs uppercase text-slate-500">On hand</p><p className="mt-1 font-heading text-xl font-bold text-slate-900">{m.on_hand} {m.unit}</p></Card>
        <Card className="p-4"><p className="text-xs uppercase text-slate-500">Avg rate</p><p className="mt-1 font-heading text-xl font-bold text-slate-900">{inr(m.avg_rate)}</p></Card>
        <Card className="p-4"><p className="text-xs uppercase text-slate-500">Stock value</p><p className="mt-1 font-heading text-xl font-bold text-emerald-600">{inr(m.value)}</p></Card>
        <Card className="p-4"><p className="text-xs uppercase text-slate-500">Reorder level</p><p className="mt-1 font-heading text-xl font-bold text-slate-900">{m.reorder_level || "—"}</p></Card>
      </div>

      <Card className="p-4">
        <h3 className="mb-3 font-heading text-base font-semibold text-slate-900">Stock ledger</h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase text-slate-500">Type</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Ref</TableHead>
              <TableHead className="text-right text-xs uppercase text-slate-500">In</TableHead>
              <TableHead className="text-right text-xs uppercase text-slate-500">Out</TableHead>
              <TableHead className="text-right text-xs uppercase text-slate-500">Balance</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">By</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(m.ledger || []).map((l) => (
                <TableRow key={l.id} data-testid={`ledger-row-${l.ref_no}`}>
                  <TableCell><Badge className={`hover:bg-inherit ${TYPE_STYLE[l.type] || "bg-slate-100 text-slate-600"}`}>{l.type}</Badge></TableCell>
                  <TableCell className="text-slate-700">{l.ref_no}</TableCell>
                  <TableCell className="text-right tabular-nums text-emerald-600">{l.qty_in || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums text-rose-600">{l.qty_out || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-700">{l.balance_after} {m.unit}</TableCell>
                  <TableCell className="text-slate-500">{l.by}<span className="block text-xs text-slate-400">{new Date(l.at).toLocaleString()}</span></TableCell>
                </TableRow>
              ))}
              {(m.ledger || []).length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-slate-400">No movements yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
